import { Box, Button, Typography } from '@mui/material';
import { ActionButton } from '../../AdditionalFiltersMenuComponent';
import { getColor } from '../../MultiSelectList';
import {
    faChevronDown,
    faChevronUp,
    faRemove,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const blueColor = 'rgb(14, 22, 100) !important';
export const countSelectedCategoryItems = (
    items: { label: string; code: string; items?: any[] }[] | undefined,
    selectedCategories: string[] | undefined,
    value?: string
): number => {
    if (!items || items.length === 0) {
        return 0;
    }
    
    if (!selectedCategories) {
        return 0;
    }

    return items.reduce((count, child) => {
        const isSelected = selectedCategories.includes(child.code) ? 1 : 0;
        const nestedCount = countSelectedCategoryItems(
            child.items,
            selectedCategories
        );
        return count + isSelected + nestedCount;
    }, 0);
};

export const CategorySection = ({
    selectedValues,
    expanded,
    items,
    handleCategorySelection,
    handleItemClick,
    toggleSection,
    handleSelectAllClearAllButtons,
    translate,
    expandedCategories,
    toggleShowMoreCategories,
}: {
    selectedValues: Record<string, string[]>;
    expanded: Record<string, boolean>;
    toggleSection: (label: string) => void;
    handleItemClick: any;
    items: {
        label: string;
        items: any[];
        code: string;
    }[];
    handleCategorySelection: any;
    handleSelectAllClearAllButtons: any;
    translate: any;
    expandedCategories: any;
    toggleShowMoreCategories: any;
}) => {
    const esstTypes = ['Make', 'Year', 'Model', 'Component'];

    const renderCategory = (
        category: {
            label: string;
            items?: any[];
            code: string;
        },
        depth: number,
        rootCategoryLabel?: string
    ) => {
        const currentRootLabel = rootCategoryLabel || category.label;
        const ITEMS_TO_SHOW = 6;
        // Handle null expandedCategories
        const isExpanded = expandedCategories && category.label ? expandedCategories[category.label] ?? false : false;
        // Handle null category.items and filter out items with null/undefined labels
        const categoryItems = (category.items || []).filter((item: any) => item.label != null);
        const sortedCategoryItems = category.label === 'Year'
            ? [...categoryItems].sort((a: any, b: any) => b.label.localeCompare(a.label))
            : categoryItems;
        const visibleItems = isExpanded
            ? sortedCategoryItems
            : sortedCategoryItems.slice(0, ITEMS_TO_SHOW);
        // Handle null handleSelectAllClearAllButtons
        const { selectionState: categorySelectionState = undefined } = 
            (handleSelectAllClearAllButtons && typeof handleSelectAllClearAllButtons === 'function') 
            ? (handleSelectAllClearAllButtons(category.code, items) ?? {}) 
            : {};
        return (
            <Box
                key={category.label}
                width={'100%'}
                id={
                    depth == 0
                        ? `categoryMenuOption_${category.label.replaceAll(
                              ' ',
                              ''
                          )}`
                        : `subcategoryMenuOption_${category.label.replaceAll(
                              ' ',
                              ''
                          )}`
                }
            >
                <Box
                    sx={{
                        border: '1px solid gray',
                        borderRadius: '4px',
                        padding: '5px 10px',
                        width: 'auto',
                        textAlign: 'center',
                        flexBasis: '100%',
                        wordBreak: 'break-word',
                        backgroundColor:
                            countSelectedCategoryItems(
                                category.items,
                                selectedValues['category'],
                                category.label
                            ) > 0
                                ? '#066FEF'
                                : 'white',
                        cursor: 'pointer',
                        textTransform: 'none',
                        color: getColor(
                            countSelectedCategoryItems(
                                category.items,
                                selectedValues['category'],
                                category.label
                            ) > 0
                        ),
                    }}
                    onClick={() => toggleSection(category.label)}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            width: '100%',
                            wordBreak: 'break-word',
                            textAlign: 'left',
                        }}
                    >
                        {countSelectedCategoryItems(
                            category.items,
                            selectedValues['category'],
                            category.label
                        ) > 0 && (
                            <Typography
                                sx={{
                                    border: '1px solid',
                                    borderRadius: '50%',
                                    minWidth: '30px',
                                    minHeight: '30px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontSize: '12px',
                                }}
                            >
                                {countSelectedCategoryItems(
                                    category.items,
                                    selectedValues['category'],
                                    category.label
                                )}
                            </Typography>
                        )}

                        <Typography>{category.label}</Typography>
                        <FontAwesomeIcon
                            icon={
                                expanded[category.label]
                                    ? faChevronUp
                                    : faChevronDown
                            }
                        />
                    </Box>
                </Box>

                {expanded[category.label] && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent:
                                    categorySelectionState === 'noneSelected'
                                        ? 'flex-end'
                                        : 'space-between',
                                marginBottom: '10px',
                                marginTop: '10px',
                                gap: '10px',
                                flexWrap: 'wrap',
                                width: '100%',
                            }}
                        >
                            {(() => {
                                if (categorySelectionState === 'no Button') {
                                    return null;
                                }
                                if (categorySelectionState === 'allSelected') {
                                    return (
                                        <ActionButton
                                            onClick={() =>
                                                handleCategorySelection(
                                                    category.code,
                                                    false
                                                )
                                            }
                                            buttonType="clearAll"
                                            buttonId={`${category.label.replaceAll(
                                                ' ',
                                                ''
                                            )}_clearAll`}
                                        >
                                            {translate(`ltl_ClearAllText`)}{' '}
                                        </ActionButton>
                                    );
                                } else if (
                                    categorySelectionState === 'someSelected'
                                ) {
                                    return (
                                        <>
                                            <ActionButton
                                                onClick={() =>
                                                    handleCategorySelection(
                                                        category.code,
                                                        false
                                                    )
                                                }
                                                buttonType="clearAll"
                                                buttonId={`${category.label.replaceAll(
                                                    ' ',
                                                    ''
                                                )}_clearAll`}
                                            >
                                                {translate(`ltl_ClearAllText`)}{' '}
                                            </ActionButton>
                                            <ActionButton
                                                onClick={() =>
                                                    handleCategorySelection(
                                                        category.code,
                                                        true
                                                    )
                                                }
                                                buttonType="selectAll"
                                                buttonId={`${category.label.replaceAll(
                                                    ' ',
                                                    ''
                                                )}_selectAll`}
                                            >
                                                {translate('selectAll_txt')}
                                            </ActionButton>
                                        </>
                                    );
                                } else if (
                                    categorySelectionState === 'noneSelected'
                                ) {
                                    return (
                                        <ActionButton
                                            onClick={() =>
                                                handleCategorySelection(
                                                    category.code,
                                                    true
                                                )
                                            }
                                            buttonType="selectAll"
                                            buttonId={`${category.label.replaceAll(
                                                ' ',
                                                ''
                                            )}_selectAll`}
                                        >
                                            {translate('selectAll_txt')}
                                        </ActionButton>
                                    );
                                }
                            })()}
                        </Box>

                        <Box
                            sx={{
                                padding: '10px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                backgroundColor: 'white',
                                alignItems: 'flex-start',
                                maxHeight: (isExpanded && depth != 0) ? '400px' : 'auto',
                                overflowY: (isExpanded && depth != 0) ? 'auto' : 'hidden',
                            }}
                        >
                            {visibleItems.map((child: any) => {
                                if (child.items && child.items.length > 0) {
                                    return renderCategory(child, depth + 1, currentRootLabel);
                                }

                                return (
                                    <Button
                                        key={String(child.label)}
                                        style={{
                                            border: '1px solid gray',
                                            borderRadius: '4px',
                                            padding: '5px 10px',
                                            width: 'auto',
                                            justifyContent: 'start',
                                            textAlign: 'left',
                                            wordBreak: 'break-word',
                                            backgroundColor: selectedValues[
                                                'category'
                                            ].includes(child.code)
                                                ? '#066FEF'
                                                : 'white',
                                            cursor: 'pointer',
                                            textTransform: 'capitalize',
                                            color: getColor(
                                                selectedValues[
                                                    'category'
                                                ].includes(child.code)
                                            ),
                                            ...(
                                                !(
                                                    category.label === 'Make' ||
                                                    category.label === 'Year' ||
                                                    category.label === 'Model'
                                                ) && {
                                                    flexBasis: '100%',
                                                }
                                            )
                                        }}
                                        id={`subcategoryMenuOption_${child.label.replaceAll(
                                            ' ',
                                            ''
                                        )}`}
                                        onClick={() => {
                                            // Handle ESST types based on parent category label
                                            if (esstTypes.includes(category.label)) {
                                                const label = `${currentRootLabel} > ${child.label}`;
                                                const type = category.label.toLowerCase();

                                                // Call handleItemClick for the specific type
                                                handleItemClick(type, label, category.code);
                                            }

                                            // Call handleItemClick for 'category'
                                            handleItemClick('category', child.code, category.code);
                                        }}
                                        startIcon={
                                            selectedValues['category'].includes(
                                                String(child.code)
                                            ) ? (
                                                <FontAwesomeIcon
                                                    icon={faRemove}
                                                    size="xs"
                                                    style={{ fontSize: '10px' }}
                                                />
                                            ) : null
                                        }
                                    >
                                        {child.label}
                                    </Button>
                                );
                            })}
                        </Box>
                        {(category?.items ?? []).length > ITEMS_TO_SHOW && (
                            <Button
                                onClick={() =>
                                    toggleShowMoreCategories(category.label)
                                }
                                style={{
                                    marginTop: 1,
                                    fontSize: '10px',
                                    fontWeight: '900',
                                    color: blueColor,
                                    marginRight: 'auto',
                                }}
                            >
                                {isExpanded
                                    ? translate(`less_txt`)
                                    : translate(`more_txt`)}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
            }}
        >
            {items && Array.isArray(items) && items.map((category) => renderCategory(category, 0))}
        </Box>
    );
};
